import { Flex, DatePicker, Card, Button, Select } from "antd"
import { useTranslation } from "react-i18next"
import SearchInput from "ui/SearchInput"
import { Title } from "ui/Typography"
import { useCallback, useEffect, useMemo, useState } from "react"
import TextHistoryTable from "./TextHistoryTable"
import WithEmptyState from "ui/WithEmptyStateWrapper"
import { useDispatch, useSelector } from "react-redux"
import { getTextLogs } from "store/clients"
import { debounce } from "lodash"
import moment from "moment"
import TemplateView from "../TemplateView"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import styles from "../index.module.css"
import PropTypes from "prop-types"
import { Announcement } from "ui/Icon/DSIcon/Announcement"

const { RangePicker } = DatePicker
const dateFormat = "MM/DD/YYYY"

const TextsTab = ({ active, id }) => {
  const { t } = useTranslation()
  const [searchBy, setSearchBy] = useState("")
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState("")
  const [sortOrder, setSortOrder] = useState("")
  const [dateRange, setDateRange] = useState(null)
  const [isAppend, setIsAppend] = useState(false)
  const dispatch = useDispatch()
  const { data, metadata } = useSelector((store) => store.clients.textLogs)
  const client = useSelector((state) => state.clients.client)

  const [selectedTextLog, setSelectedTextLog] = useState(null)
  const navigate = useNavigate()

  useLoaderWatchers({ watchers: ["clients.textLogs"] })

  // Debounced search handler
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value)
        setPage(1)
      }, 400),
    []
  )

  // Effect to fetch text logs
  useEffect(() => {
    const fetchTextLogs = async () => {
      if (active && id) {
        const queryParams = new URLSearchParams()
        queryParams.append("page", page)
        queryParams.append("per_page", pageSize)
        if (searchQuery) queryParams.append("search_term", searchQuery)
        if (sortKey) {
          const formattedSortKey = "sort_by_date"
          queryParams.append(formattedSortKey, sortOrder || "desc")
        }
        if (dateRange?.startDate && dateRange?.endDate) {
          queryParams.append("start_date", moment(dateRange.startDate).format("YYYY-MM-DD"))
          queryParams.append("end_date", moment(dateRange.endDate).format("YYYY-MM-DD"))
        }
        await dispatch(getTextLogs(id, Object.fromEntries(queryParams), isAppend))
      }
    }
    fetchTextLogs()
  }, [active, id, page, pageSize, sortKey, sortOrder, searchQuery, dateRange, dispatch, isAppend])

  // Handlers
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target ? e.target.value : e
      setSearchBy(value)
      debouncedSetSearchQuery(value)
    },
    [debouncedSetSearchQuery]
  )

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size)
    setPage(1)
  }, [])

  const onSortChange = useCallback((pagination, filters, sorter) => {
    setSortKey(sorter?.field)
    setSortOrder(sorter?.order === "ascend" ? "asc" : "desc")
    setPage(1)
  }, [])

  const handleDateChange = useCallback((_, dateStrings) => {
    if (dateStrings && dateStrings.length === 2 && dateStrings[0] && dateStrings[1]) {
      setDateRange({
        startDate: dateStrings[0],
        endDate: dateStrings[1]
      })
    } else {
      setDateRange(null)
    }
    setPage(1)
  }, [])

  const onRowClick = (selectedTextLogId) => {
    const textLog = data.find((text) => text.id === selectedTextLogId)
    setSelectedTextLog(textLog)
  }

  const handleNewTextClick = () => {
    navigate(routes.guideClientTextActionPath(), {
      state: { clients: [client], userIds: [client?.user?.id], templates: client?.text_templates_collection, fromTab: "text" }
    })
  }
  const handleSortChange = (value) => {
    const [key, order] = value.split("#")
    setSortKey(key)
    setSortOrder(order)
    setPage(1)
  }

  if (selectedTextLog) {
    return (
      <TemplateView
        createdAt={selectedTextLog?.timestamp}
        selectedTemplateLog={selectedTextLog}
        setSelectedTemplateLog={setSelectedTextLog}
      />
    )
  }

  return (
    <FirstLoading name="clients.textLogs">
      <Card>
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="center">
            <Title level={5}>{t("client.text_history")}</Title>
            <Button type="primary" className={`${styles.ShowOnTab} px-xl py-l ml-auto`} onClick={handleNewTextClick}>
              New Text
            </Button>
          </Flex>
          <Flex gap={16} className="w-full" wrap="wrap">
            <SearchInput
              className={styles.clientSearchInput}
              searchBy={searchBy}
              setSearchBy={handleSearchChange}
              ariaLabel={t("clients.aria.search", { name: "text-history" })}
              ariaControls={t("clients.aria.search_result", { name: "text-history" })}
              placeholder={t("global.search")}
            />
            <RangePicker
              className={styles.clientRangePicker}
              format={dateFormat}
              onChange={handleDateChange}
              placeholder={["From", "To"]}
            />
            <Select className={styles.clientSortByFilter} placeholder="Sort By" onChange={handleSortChange}>
              <Select.Option value="sort_by_date#asc">Date sent (A to Z)</Select.Option>
              <Select.Option value="sort_by_date#desc">Date sent (Z to A)</Select.Option>
            </Select>
            <Button type="primary" className={`${styles.hideOnTab} px-xl py-l ml-auto`} onClick={handleNewTextClick}>
              New Text
            </Button>
          </Flex>
          <WithEmptyState
            name="Text"
            hasItems={!!data?.length}
            fallbackTitle={t("client.no_text_history_yet")}
            fallbackImageSrc={<Announcement />}
          >
            <TextHistoryTable
              data={data}
              metadata={metadata}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={onSortChange}
              onRowClick={onRowClick}
              setIsAppend={setIsAppend}
            />
          </WithEmptyState>
        </Flex>
      </Card>
    </FirstLoading>
  )
}

TextsTab.propTypes = {
  active: PropTypes.bool,
  id: PropTypes.string
}

export default TextsTab
